import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, FolderKanban, Users, AlertCircle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { projectService } from '../../../services/projectService';
import { userService } from '../../../services/userService';

const ProjectManagement = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    clientId: '',
    startDate: '',
    estimatedEndDate: '',
    status: 'ativo'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsData, clientsData] = await Promise.all([
        projectService?.getAllProjects(),
        userService?.getClientUsers()
      ]);
      setProjects(projectsData);
      setClients(clientsData);
      setError('');
    } catch (err) {
      setError('Erro ao carregar dados: ' + err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');

    try {
      if (editingProject) {
        await projectService?.updateProject(editingProject?.id, formData);
      } else {
        await projectService?.createProject(formData);
      }
      await loadData();
      setShowModal(false);
      resetForm();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDelete = async (projectId) => {
    if (!confirm('Tem certeza que deseja excluir este projeto?')) return;

    try {
      await projectService?.deleteProject(projectId);
      await loadData();
    } catch (err) {
      setError('Erro ao excluir projeto: ' + err?.message);
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      name: project?.name,
      description: project?.description || '',
      clientId: project?.clientId || '',
      startDate: project?.startDate?.split('T')?.[0] || '',
      estimatedEndDate: project?.estimatedEndDate?.split('T')?.[0] || '',
      status: project?.status
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      clientId: '',
      startDate: '',
      estimatedEndDate: '',
      status: 'ativo'
    });
    setEditingProject(null);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-muted-foreground">Carregando projetos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-foreground">Gerenciar Projetos</h2>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Projeto
        </button>
      </div>
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {projects?.map((project) => (
          <div key={project?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <FolderKanban className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">{project?.name}</h3>
                  <p className="text-xs text-muted-foreground">{project?.status}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => navigate(`/admin/projeto/${project?.id}`)}
                  className="p-1.5 text-primary hover:bg-primary/10 rounded transition-colors"
                >
                  <Calendar className="h-4 w-4" />
                </button>
                <button
                  onClick={() => openEditModal(project)}
                  className="p-1.5 text-primary hover:bg-primary/10 rounded transition-colors"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(project?.id)}
                  className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {project?.description && (
              <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                {project?.description}
              </p>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4" />
                <span>{project?.clientName || 'Sem cliente'}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="h-4 w-4" />
                <span>
                  {new Date(project.startDate)?.toLocaleDateString('pt-BR')} - 
                  {new Date(project.estimatedEndDate)?.toLocaleDateString('pt-BR')}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
      {projects?.length === 0 && (
        <div className="text-center py-12 bg-card border border-border rounded-lg">
          <FolderKanban className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">Nenhum projeto cadastrado</p>
        </div>
      )}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-card border border-border rounded-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-foreground mb-4">
              {editingProject ? 'Editar Projeto' : 'Novo Projeto'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Nome do Projeto
                </label>
                <input
                  type="text"
                  value={formData?.name}
                  onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Descrição
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Cliente Responsável *
                </label>
                <select
                  value={formData?.clientId}
                  onChange={(e) => setFormData({ ...formData, clientId: e?.target?.value })}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  required
                >
                  <option value="">Selecione um cliente</option>
                  {clients?.map((client) => (
                    <option key={client?.id} value={client?.id}>
                      {client?.fullName} ({client?.email})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Apenas usuários com perfil Cliente podem ser vinculados
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Data de Início
                  </label>
                  <input
                    type="date"
                    value={formData?.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Prazo Final Estimado
                  </label>
                  <input
                    type="date"
                    value={formData?.estimatedEndDate}
                    onChange={(e) => setFormData({ ...formData, estimatedEndDate: e?.target?.value })}
                    className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Status
                </label>
                <select
                  value={formData?.status}
                  onChange={(e) => setFormData({ ...formData, status: e?.target?.value })}
                  className="w-full px-3 py-2 bg-background border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
                  required
                >
                  <option value="ativo">Ativo</option>
                  <option value="pausado">Pausado</option>
                  <option value="concluido">Concluído</option>
                  <option value="cancelado">Cancelado</option>
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  {editingProject ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectManagement;